/**
 * ConfirmDestructiveDialog — shadcn <AlertDialog> wrapper for destructive
 * Visual Query Editor edits (currently: removing a CTE that is referenced
 * downstream). Replaces the never-existed "confirmation dialog" the old
 * CteSection header comment falsely claimed.
 *
 * PRD: Ralph/VqeUxRefinement/PRD.md — UX-4 (<AlertDialog> replaces
 * window.confirm) + UX-7 (destructive CTE remove must confirm).
 */
import { useRef } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';

export interface ConfirmDestructiveDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  /** Label for the cancel (dismiss) button. Defaults to "Cancel". */
  readonly cancelLabel?: string;
  readonly testId: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ConfirmDestructiveDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  testId,
  onConfirm,
  onCancel,
}: ConfirmDestructiveDialogProps) {
  // Radix closes the dialog on BOTH button clicks AND dismissal (Escape /
  // outside / Cancel), each routing through `onOpenChange(false)`. Settling on
  // both the button `onClick` AND `onOpenChange` would double-fire (e.g. a
  // confirm followed by a spurious cancel). We record the intent on the
  // action button and let the single `onOpenChange(false)` settle exactly once,
  // defaulting to cancel for any dismissal (Escape / outside / Cancel button).
  const confirmIntentRef = useRef(false);

  const handleOpenChange = (isOpen: boolean): void => {
    if (isOpen) return;
    const confirmed = confirmIntentRef.current;
    confirmIntentRef.current = false;
    if (confirmed) onConfirm();
    else onCancel();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              confirmIntentRef.current = true;
            }}
            className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
            data-testid={`${testId}-apply`}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
